<template>
  <div class="gm-email-sender">
    <div class="shared-sender-tabs" role="tablist" aria-label="Sender application">
      <button id="groovemail-senders-tab" type="button" role="tab" :aria-selected="senderSource === 'groovemail'" aria-controls="groovemail-senders-panel" :tabindex="senderSource === 'groovemail' ? 0 : -1" @keydown.right.prevent="selectSenderSource('mailbaser')" @keydown.left.prevent="selectSenderSource('mailbaser')" @click="selectSenderSource('groovemail')">GrooveMail</button>
      <button id="mailbaser-senders-tab" type="button" role="tab" :aria-selected="senderSource === 'mailbaser'" aria-controls="mailbaser-senders-panel" :tabindex="senderSource === 'mailbaser' ? 0 : -1" @keydown.right.prevent="selectSenderSource('groovemail')" @keydown.left.prevent="selectSenderSource('groovemail')" @click="selectSenderSource('mailbaser')">MailBaser</button>
    </div>
    <div v-if="senderSource === 'mailbaser'" id="mailbaser-senders-panel" role="tabpanel" aria-labelledby="mailbaser-senders-tab"><SharedMailBaserSenders @linked="refreshLinkedSenders" /></div>
    <div v-show="senderSource === 'groovemail'" id="groovemail-senders-panel" role="tabpanel" aria-labelledby="groovemail-senders-tab">
    <div id="groovemail-client-table-view">
      <client-table
        class="groovemail-email-sender-table"
        :tableRef="'emailSenderTable'"
        :tableData="senders"
        :tableColumns="SettingTableColumns"
        :tableOptions="SettingTableOptions"
        :tableCondition="senders.length"
        :tableTitle="'frontend_groovemail_settings_sender_settings'"
        :slotData="emailSenderSlotData"
        :emptyStateData="emailSenderEmptyStateData"
        :addNewBtnText="'frontend_groovemail_segment_new_sender'"
        role="region"
        aria-label="Email sender table"
        tabindex="0"
        @addNewData="openCreateSetting"
      >
      </client-table>
    </div>

    </div>

    <gd-modal
      id="webhook-url-modal"
      name="webhook-url-modal"
      class="gm-email-webhook-modal"
      :title="
        $t('frontend_groovemail_senders_webhook_url') +
          ' ' +
          $t('frontend_groovemail_senders_webhook_selected_events')
      "
    >
      <div class="p-6">
        <div class="flex gm-email-webhook-input">
          <text-input
            variant="base"
            v-model="webhookUrl"
            :disabled="true"
            :placeholder="$t('frontend_product_step8_webhook')"
          />
        </div>
        <div class="flex justify-end mt-6 gm-email-webhook-actions">
          <gd-button
            size="auto"
            @click="closeWebhookUrlModal"
            >{{ $t('frontend_cancel') }}</gd-button
          >
          <gd-button
            v-clipboard:copy="webhookUrl"
            v-clipboard:success="onCopyWeebhookUrl"
            v-clipboard:error="onErrorWebhookUrl"
            class="ml-2"
            variant="primary"
            size="auto"
          >
            {{ $t('frontend_copy') }}
          </gd-button>
        </div>
      </div>
    </gd-modal>
  </div>
</template>

<script>
import SharedMailBaserSenders from './SharedMailBaserSenders.vue';
import Notify from '@groovepages/gd-ui-kit/notify';
import ClientTable from '@groovepages/gd-ui-kit/components/tables/ClientTable.vue';
import getSmtpDriverName from '../getSmtpDriverName';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';
import { sendersUrl } from '@/utils/urls.js';
import { getData } from '@/utils/apiHelper.js';

export default {
  name: 'EmailSender',
  props: ['senders', 'userCanMail'],

  mixins: [demoModeValuesMixin],

  data() {
    return {
      senderSource: 'groovemail',
      webhookUrl: '',
      emailSenderEmptyStateData: {
        emptyStateHeading: 'frontend_groovemail_sender_title',
        emptyStateSubHeading:
          'frontend_groovemail_settings_sender_dont_have_sender',
        emptyStateBtnText: 'frontend_groovemail_segment_new_sender'
      },
      SettingTableColumns: [
        'name',
        'sender_name',
        'sender_email',
        'reply_email',
        'driver',
        'is_verified',
        'is_default',
        'actions'
      ],
    };
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    /**
     * Used to return table options.
     * @returns {object} returns table options.
    */
    SettingTableOptions() {
      return {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc'
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query')
        },
        sortable: ['name', 'sender_name', 'sender_email', 'reply_email'],
        filterable: ['name', 'sender_name', 'sender_email', 'reply_email'],
        headings: {
          is_verified: 'Verified',
          is_default: 'Default'
        },
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
      };
    },

    /**
     * Used to return slot data.
     * @returns {Array} returns slot data.
    */
    emailSenderSlotData() {
      return [
        {
          slotName: 'name',
          slotType: 'computed',
          value: props => this.demoMode ? this.basicText : props.row.name,
        },
        {
          slotName: 'sender_name',
          slotType: 'computed',
          value: props => this.demoMode ? this.basicText : props.row.sender_name,
        },
        {
          slotName: 'sender_email',
          slotType: 'computed',
          value: props => this.demoMode ? this.basicEmailText : props.row.sender_email,
        },
        {
          slotName: 'reply_email',
          slotType: 'computed',
          value: props => this.demoMode ? this.basicEmailText : props.row.reply_email,
        },
        {
          slotName: 'driver',
          slotType: 'computed',
          value: props => {
            return this.getDriver(props.row.driver);
          }
        },
        {
          slotName: 'is_verified',
          slotType: 'computed',
          value: props => props.row.is_verified === 1 ? 'Yes' : 'No',
        },
        {
          slotName: 'is_default',
          slotType: 'computed',
          value: props => props.row.is_default ? 'Yes' : 'No',
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          actionList: [
            {
              id: 0,
              name: 'frontend_edit',
              conditions: () => this.userCanMail !== 0,
              callback: props => Number(props.row.driver) === 7 ? this.selectSenderSource('mailbaser') : this.openEditSetting(props.row.id)
            },
            {
              id: 1,
              name: 'frontend_groovemail_senders_smtp_get_webhook_url_action',
              conditions: row => row.driver === 2 || row.driver === 3 || row.driver === 4 || row.driver === 5,
              callback: props => this.showWebhookUrl(props.row)
            },
            {
              id: 2,
              name: 'frontend_groovemail_sender_listing_reverification',
              conditions: row => this.userCanMail !== 0 && row.is_verified !== 1 && Number(row.driver) !== 7,
              callback: props => this.resendVerificationMail(props.row.id)
            },
            {
              id: 3,
              name: 'frontend_delete',
              conditions: () => true,
              callback: props => this.deleteSetting('email', props.row.id)
            }
          ],
          style: props => props.row.is_verified !== 1 ? 'width:175px' : '',
        }
      ];
    }
  },

  components: {
    SharedMailBaserSenders,
    ClientTable
  },

  methods: {
    refreshLinkedSenders() {
      // The settings page owns the native catalog; reload it after linking.
      this.$emit('senderLinked');
    },
    selectSenderSource(source) {
      this.senderSource = source;
      this.$nextTick(() => document.getElementById(`${source}-senders-tab`).focus());
    },
    // Used to create new email sender. Redirects user to create sender page if user can mail else displays error message.
    openCreateSetting() {
      if (this.userCanMail) {
        this.$router.push({ name: 'NewSender' });
      } else {
        this.$emit('showError');
      }
    },

    /**
     * Used to edit email-sender. Redirects user to edit sender page if user can mail else displays error message.
     * @param {string} id contains unique id of email sender.
     */
    openEditSetting(id) {
      if (this.userCanMail) {
        this.$router.push({ name: 'EditSender', params: { id } });
      } else {
        this.$emit('showError');
      }
    },

    // Opens a modal which displays webhook url.
    openWebhookUrlModal() {
      this.$root.$emit('modal-open', 'webhook-url-modal');
    },

    // Closed modal which displays webhook url.
    closeWebhookUrlModal() {
      this.$root.$emit('modal-close', 'webhook-url-modal');
    },

    /**
     * Opens a modal which displays webhook url.
     * @param {object} senderData contains details of sender.
     */
    showWebhookUrl(senderData) {
      this.getWebhookUrl(senderData);
      this.openWebhookUrlModal();
    },

    /**
     * Sets webhook url based on sender smtp driver.
     * @param {object} senderData contains details of sender.
     */
    getWebhookUrl(senderData) {
      const gdMailAPIsUrl = window.config.apiUrl;
      const smtpDriver = senderData.driver;
      
      switch (smtpDriver) {
        case 2:
          this.webhookUrl = `${gdMailAPIsUrl}/handle-mailgun-webhook/${this.$store.state.user.id}`;
          break;
        case 3:
          this.webhookUrl = `${gdMailAPIsUrl}/handle-sendgrid-webhook/${this.$store.state.user.id}`;
          break;
        case 4:
          this.webhookUrl = `${gdMailAPIsUrl}/handle-sparkpost-webhook/${this.$store.state.user.id}`;
          break;
        case 5:
          this.webhookUrl = `${gdMailAPIsUrl}/handle-ses-webhook/${this.$store.state.user.id}`;
          break;
        default:
          this.webhookUrl = '';
      }
    },

    // Shows success notification when webhook url is copied successfully
    onCopyWeebhookUrl() {
      Notify.success(
        this.$t('frontend_groovemail_senders_webhook_url_copy_success')
      );
    },

    // Shows error notification when webhook url is not copied successfully
    onErrorWebhookUrl() {
      Notify.fail(
        this.$t('frontend_groovemail_senders_webhook_url_copy_error')
      );
    },

    /**
     * Used to resend verification mail.
     * @param {string} id contains unique id of email sender.
     */
    async resendVerificationMail(id) {
      const resp = await getData(`${sendersUrl.senderEmailVerification}/${id}`);
      if (resp?.data?.status === 200) {
        Notify.success(resp?.data?.message);
      } else if (resp?.data?.status === 403) {
        Notify.fail(this.$t('frontend_groovemail_senders_resend_verification_mail_after_10_min_message'));
      } else if (resp?.data?.status === 406) {
        Notify.fail(this.$t('frontend_groovemail_senders_resend_verification_mail_1_month_time_expired'));
      }
    },

    /**
     * Emits deleteSetting event to delete email-sender.
     * @param {string} type contains type of sender.
     * @param {string} id contains unique id of email sender.
     */
    deleteSetting(type, id) {
      this.$emit('deleteSetting', type, id);
    },
    /**
     * Used to return smtp driver name based on smtp driver value.
     * @param {number} driver smtp driver value.
     * @returns {string} returns smtp driver name.
     */
    getDriver(driver) {
      return getSmtpDriverName(driver);
    }
  }
};
</script>

<style lang="scss">
.groovemail-email-sender-table {
  .VueTables--client table {
    overflow: auto !important;
    th:nth-child(1),
    td:nth-child(1),
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4) {
      width: 18%;
      word-break: break-word;
    }
  }
  .VueTables__table {
    overflow: auto !important;
  }
  .VueTables__search-field {
    margin-bottom: 85px !important;
  }
  .VueTables__limit {
    bottom: -60px !important;
  }
}
</style>

<style scoped>
.gm-email-sender,
.gm-email-sender ::v-deep #groovemail-client-table-view,
.gm-email-sender ::v-deep #gd-client-table-view,
.gm-email-sender ::v-deep .VueTables--client {
  min-width: 0;
  max-width: 100%;
}

/* ClientTable keeps the title, search, Add and page-size controls outside its
 * table-responsive node. Scope horizontal reachability to that node so those
 * existing controls remain directly reachable at every width. */
.gm-email-sender ::v-deep .table-responsive {
  clear: both;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gm-email-sender ::v-deep .table-responsive:focus-within {
  outline: 0;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gm-email-sender ::v-deep .VueTables__table {
  min-width: 1060px;
  table-layout: fixed;
}

.gm-email-sender ::v-deep .VueTables__table th,
.gm-email-sender ::v-deep .VueTables__table td {
  vertical-align: middle;
  border-color: var(--gs-border);
}

.gm-email-sender ::v-deep .VueTables__table th:nth-child(1),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(1) {
  width: 16%;
}

.gm-email-sender ::v-deep .VueTables__table th:nth-child(2),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(2) {
  width: 14%;
}

.gm-email-sender ::v-deep .VueTables__table th:nth-child(3),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(3),
.gm-email-sender ::v-deep .VueTables__table th:nth-child(4),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(4) {
  width: 16%;
}

.gm-email-sender ::v-deep .VueTables__table th:nth-child(5),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(5) {
  width: 10%;
}

.gm-email-sender ::v-deep .VueTables__table th:nth-child(6),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(6),
.gm-email-sender ::v-deep .VueTables__table th:nth-child(7),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(7) {
  width: 7%;
}

.gm-email-sender ::v-deep .VueTables__table th:nth-child(8),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(8) {
  width: 14%;
  min-width: 148px;
  white-space: nowrap;
}

.gm-email-sender ::v-deep .VueTables__table td:nth-child(1),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(2),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(3),
.gm-email-sender ::v-deep .VueTables__table td:nth-child(4) {
  overflow-wrap: anywhere;
  word-break: normal;
}

/* ClientTable provides action items as plain divs. Keep that actual control
 * and its callbacks, while giving only this sender table's visible actions a
 * touch-sized target and a destructive color for the fixed fourth Delete item. */
.gm-email-sender ::v-deep .VueTables__table td:nth-child(8) .absolute.pin-r > .bg-white > div > div {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 12px;
}

.gm-email-sender ::v-deep .VueTables__table td:nth-child(8) .absolute.pin-r > .bg-white > div:nth-child(4) > div {
  color: var(--gs-danger);
}

/* This exact source modal is a sibling of the table inside EmailSender. Keep
 * its existing root-event, disabled input, clipboard directive, and buttons;
 * this class is only a local presentation hook for the two installed modal
 * wrapper shapes used by the fixture/runtime families. */
.gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal > .gd-modal__content > div,
.gm-email-sender ::v-deep .gm-email-webhook-modal.gd-modal > .max-h-full > .bg-white {
  width: min(600px, calc(100vw - 24px)) !important;
  max-width: calc(100vw - 24px) !important;
}

.gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .gm-email-webhook-input > div,
.gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .gm-email-webhook-input input {
  min-height: 44px;
}

.gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .gm-email-webhook-input > div {
  height: 46px !important;
  min-height: 46px;
}

.gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .gm-email-webhook-input input {
  height: 44px !important;
}

.gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .gm-email-webhook-actions > button {
  height: 44px !important;
  min-height: 44px;
}

.gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .gm-email-webhook-actions > button:not(.ml-2) {
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border-color: var(--gs-border);
  box-shadow: none !important;
}

.gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .gm-email-webhook-actions > button.ml-2 {
  min-width: 44px;
  min-height: 44px;
  padding-left: 16px;
  padding-right: 16px;
  color: var(--gs-primary-foreground);
  background: var(--gs-primary);
  border-color: var(--gs-primary);
  border-radius: var(--gs-radius-md);
}

@media (max-width: 640px) {
  .gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal > .gd-modal__content {
    padding: 12px !important;
  }

  .gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .border-b.h-16 {
    height: auto;
    min-height: 44px;
    padding: 10px 12px;
    align-items: flex-start;
  }

  .gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .border-b.h-16 > span:first-child {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .border-b.h-16 > span:last-child {
    display: inline-flex;
    flex: 0 0 44px;
    width: 44px;
    min-width: 44px;
    min-height: 44px;
    align-items: center;
    justify-content: center;
    margin: -10px -12px -10px 0;
  }

  .gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .p-6 {
    padding: 16px;
  }

  .gm-email-sender ::v-deep #webhook-url-modal.gm-email-webhook-modal .gm-email-webhook-actions {
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;
  }

}

@media (max-width: 640px) {
  .gm-email-sender ::v-deep .new-add-btn-wrapper {
    position: static;
    margin-bottom: 16px;
  }

  .gm-email-sender ::v-deep .new-add-btn-wrapper > div {
    flex-wrap: wrap;
    gap: 12px;
  }

  .gm-email-sender ::v-deep .new-add-btn-wrapper > div > div:first-child,
  .gm-email-sender ::v-deep .new-add-btn-wrapper > div > div:last-child {
    width: 100%;
  }

  .gm-email-sender ::v-deep .new-add-btn-wrapper > div > div:last-child {
    justify-content: flex-start;
  }

  .gm-email-sender ::v-deep .new-add-btn {
    min-width: 148px;
    min-height: 44px;
  }

  .gm-email-sender ::v-deep #gd-client-table-view .pull-left.VueTables__search {
    float: none !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .gm-email-sender ::v-deep #gd-client-table-view .VueTables__search-field {
    width: 100% !important;
    margin-bottom: 16px !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  /* ClientTable's actual search glyph is an absolutely positioned ::before.
   * Its legacy 18px top offset assumed the input's former 10px top margin;
   * align that existing glyph with the reflowed 40px control instead. */
  .gm-email-sender ::v-deep #gd-client-table-view .VueTables__search-field::before {
    top: 8px;
    left: 12px;
    pointer-events: none;
  }

  .gm-email-sender ::v-deep #gd-client-table-view .VueTables .row,
  .gm-email-sender ::v-deep #gd-client-table-view .VueTables .row > .col-md-12 {
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .gm-email-sender ::v-deep .VueTables__limit {
    float: none;
  }

  .gm-email-sender ::v-deep #gd-client-table-view .VueTables__search__input.form-control {
    box-sizing: border-box;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding-left: 44px !important;
    min-height: 44px;
  }

  .gm-email-sender ::v-deep #gd-client-table-view .VueTables__limit select,
  .gm-email-sender ::v-deep .VueTables__table td:nth-child(8) .float-right > div[role="button"] > span,
  .gm-email-sender ::v-deep .VuePagination__pagination-item a {
    min-height: 44px;
  }

  .gm-email-sender ::v-deep .VuePagination__pagination-item a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
  }

  .gm-email-sender ::v-deep #gd-client-table-view .VueTables__limit {
    position: static !important;
    float: none !important;
    bottom: auto !important;
    margin: 0 10px 16px !important;
  }

  .gm-email-sender ::v-deep #gd-client-table-view .VueTables__limit-field {
    position: relative !important;
    display: inline-flex !important;
    align-items: center;
    min-height: 44px;
  }

  .gm-email-sender ::v-deep #gd-client-table-view .VueTables__limit-field::after {
    top: 50% !important;
    right: 0.75rem !important;
    transform: translateY(-50%);
  }

  .gm-email-sender ::v-deep #gd-client-table-view .VuePagination.row.col-md-12.justify-content-center {
    clear: both;
    margin-top: 16px !important;
  }

  .gm-email-sender ::v-deep .VueTables__table .VueTables__row:last-child td:nth-child(8) .absolute.pin-r {
    top: auto;
    bottom: calc(100% + 8px);
    margin-top: 0;
  }
}

</style>

<style scoped>
/* Keep source tabs below the existing absolutely positioned Email/SMS navigation. */
.shared-sender-tabs { display: flex; flex-wrap: wrap; gap: 8px; padding-top: 32px; margin-bottom: 16px; }
.shared-sender-tabs button { padding: 10px 16px; border-radius: 8px; border: 1px solid var(--gm-border-color, #e2e8f0); }
.shared-sender-tabs button[aria-selected="true"] { font-weight: 600; background: var(--gm-bg-secondary, #f1f5f9); }
</style>
